import { useTranslation } from 'react-i18next'
import { Link2, Plus } from 'lucide-react'
import Link from '@/routing/Link'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ROUTES } from '@/routes/routes'
import type { KnowledgeDocumentCollectionProps } from '@/types/components/knowledge-document-collection'
import { KnowledgeLibraryCard } from './KnowledgeLibraryCard'
import { KnowledgeLibraryListView } from './KnowledgeLibraryListView'

export function KnowledgeDocumentCollection({
  showDocumentSkeleton,
  filteredDocuments,
  hasActiveQueryOrFilters,
  viewMode,
  deletingToken,
  onDelete,
  onDocumentUpdated,
  onOpen,
  onAddLinks,
  onClearFilters,
}: KnowledgeDocumentCollectionProps) {
  const { t } = useTranslation('app')
  return (
    <section data-testid="knowledge-document-list">
      {showDocumentSkeleton ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[18px]">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} className="h-56 rounded-2xl" />
          ))}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <LobbyEmptyState
          title={
            hasActiveQueryOrFilters
              ? t('library.noMatches')
              : t('library.emptyTitle')
          }
          description={
            hasActiveQueryOrFilters
              ? t('library.noMatchesBody')
              : t('library.emptyBody')
          }
        >
          <div data-testid="knowledge-empty-state">
            {!hasActiveQueryOrFilters ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  shape="pill"
                  className="px-6"
                  data-testid="knowledge-empty-add-links"
                  onClick={onAddLinks}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  {t('library.addVideoOrLink')}
                </Button>
                <Button
                  asChild
                  variant="cta"
                  shape="pill"
                  className="px-6"
                  data-testid="knowledge-empty-upload"
                >
                  <Link href={ROUTES.knowledgeUpload}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('library.uploadFiles')}
                  </Link>
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                shape="pill"
                data-testid="knowledge-clear-filters"
                onClick={onClearFilters}
              >
                {t('library.clearFilters')}
              </Button>
            )}
          </div>
        </LobbyEmptyState>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[18px]">
          {filteredDocuments.map((doc) => (
            <KnowledgeLibraryCard
              key={doc.token}
              doc={doc}
              deleting={deletingToken === doc.token}
              onDelete={onDelete}
              onDocumentUpdated={onDocumentUpdated}
              onOpen={onOpen}
            />
          ))}
        </div>
      ) : (
        <KnowledgeLibraryListView
          documents={filteredDocuments}
          onDocumentUpdated={onDocumentUpdated}
          onOpen={onOpen}
        />
      )}
    </section>
  )
}
